'use client';

import type { FC } from 'react';
import type { ExhibitionGridDataType, ExhibitionGridItemType } from './types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useAtomValue } from 'jotai';
import { cn, SubHeader } from '@yanolja-rnd/nol-design-system';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { ShowAllButton } from 'app/_components/common/show-all-button';
import { pcWidthAtom } from '@/app/_shared/store/device';
import config from '@/conf';
import ExhibitionGridItem from './exhibition-grid-item';

export interface ExhibitionGridProps {
  data: ExhibitionGridDataType;
  type?: 'widget' | 'page';
  pageNameLog: HomeLogPageNameValue | undefined;
}

/**
 * 기획전 모음 위젯/전체보기
 * - 위젯과 전체보기 같은 컴포넌트 사용
 *
 * @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=7-5853&node-type=section&t=c0F9RbyVyoRLQqXN-0}
 */
export const ExhibitionGrid: FC<ExhibitionGridProps> = ({ data, type = 'widget', pageNameLog }) => {
  const { items, header, logMeta: containerLogMeta, uiExpMeta: containerUiExpMeta } = data;
  const { items: exhibitions } = items[0];

  const isPageType = type === 'page';

  const { isPcWidth } = useAtomValue(pcWidthAtom);

  const slicedExhibitions = getSlicedExhibitions(exhibitions, isPcWidth, type);

  const getMergedLogMeta = (item: ExhibitionGridItemType) => ({
    ...containerLogMeta,
    ...item.logMeta,
    uiExpMeta: { ...containerUiExpMeta, ...item.uiexpMeta },
  });

  return (
    <section
      className={cn('w-full py-16 pc:w-pc-size pc:px-0 pc:py-40', {
        'pb-80 pt-100 pc:pb-96 pc:pt-174': isPageType,
      })}>
      {!!header?.title && <SubHeader className="px-16" title={header.title} />}
      <div
        className={cn('mx-16 grid grid-cols-2 gap-1 overflow-hidden rounded-12 pc:mx-0 pc:grid-cols-3 pc:gap-8', {
          'rounded-none': isPageType,
        })}>
        {slicedExhibitions.map((item) => (
          <YaTrackerImpression
            key={item.id}
            onImpressed={() => {
              // 위젯에서만 impr 로그 수집
              !isPageType && !!pageNameLog && clientLogStandardized('impression', pageNameLog, getMergedLogMeta(item));
            }}>
            <ExhibitionGridItem
              item={item}
              type={type}
              onClick={async () => {
                await waitUntilClickTimeout(
                  clientLogStandardized(
                    'click',
                    !isPageType && !!pageNameLog ? pageNameLog : 'ExhibitionList',
                    getMergedLogMeta(item),
                  ),
                );
                location.href = `${config.service.home.origin}/exhibition/${item.id}`;
              }}
            />
          </YaTrackerImpression>
        ))}
      </div>
      {!isPageType && !!header?.more?.title ? (
        <ShowAllButton
          boldTitle={header.title}
          title={header.more.title}
          href={header.more.action.web}
          className="mt-16 pc:mt-24"
          onClick={() =>
            !!pageNameLog &&
            clientLogStandardized('click', pageNameLog, { ...header.more?.logMeta, ...containerLogMeta })
          }
        />
      ) : null}
    </section>
  );
};

const getSlicedExhibitions = (exhibitions: ExhibitionGridItemType[], isPcWidth: boolean, type?: 'widget' | 'page') => {
  if (type === 'page') {
    return exhibitions;
  }

  if (isPcWidth) {
    // PC: 6개 이하로 모두 노출한다.
    return exhibitions.slice(0, 6);
  }

  // 모바일: 6개 이하에서 짝수가 되도록 한다.
  const exhibitionsRow = Math.floor(exhibitions.length / 2);
  const maxItems = exhibitionsRow < 3 ? exhibitionsRow * 2 : 6;

  return exhibitions.slice(0, maxItems);
};
